import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  CepLookupResult,
  ClientAddressDto,
  ClientAddressInput,
  ClientAddressListItem,
} from './types';

export function createClientAddressesApi(clientId: number): CrudApi<
  ClientAddressListItem,
  ClientAddressDto,
  ClientAddressInput
> & {
  lookupCep: (cep: string) => Promise<CepLookupResult>;
} {
  const BASE = `/v1/clients/${clientId}/addresses`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<ClientAddressListItem>> {
      const { data } = await api.get<{
        data: ClientAddressListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      return normalizeListEnvelope(data);
    },

    async get(id: number): Promise<ClientAddressDto> {
      const { data } = await api.get<{ data: ClientAddressDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async create(input: ClientAddressInput): Promise<ClientAddressDto> {
      const { data } = await api.post<{ data: ClientAddressDto }>(BASE, input);
      return data.data;
    },

    async update(id: number, input: ClientAddressInput): Promise<ClientAddressDto> {
      const { data } = await api.put<{ data: ClientAddressDto }>(`${BASE}/${id}`, input);
      return data.data;
    },

    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },

    async lookupCep(cep: string): Promise<CepLookupResult> {
      const digits = cep.replace(/\D/g, '');
      const { data } = await api.get<{ data: CepLookupResult }>(`${BASE}/lookup`, {
        params: { cep: digits },
      });
      return data.data;
    },
  };
}
